import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import Styled from "styled-components";
import Theme from '../common/NekoTheme';
import { NekoModal } from "./Modal";
import { NekoTypo } from "./../misc/Typography";
import { NekoInput } from "./../form/Input";
import { NekoButton } from "../button/Button";
import { NekoPaging } from "../misc/Paging";
import NekoBusyOverlay from "../common/NekoBusyOverlay";
import { NekoIcon } from "../misc/Icon";

const StyledNekoModal = Styled(NekoModal)`
  width: 633px;

  .neko-media-library-modal-content-header {
    align-items: center;
    display: flex;
    justify-content: space-between;

    h1 {
      font-size: 21px;
      font-weight: bold;
      margin: 0;
    }

    input {
      background-color: ${Theme.white};
      border: 1px solid ${Theme.black};
      border-radius: 3px;
    }
  }
  .neko-media-library-modal-content-body {
    margin-top: 33px;
    margin-bottom: 36px;

    .neko-media-library-modal-content-body-gallery {
      display: grid;
      gap: 10px;
      grid-template-columns: repeat(6, 1fr);
      margin-bottom: 10px;

      .neko-media-library-modal-content-media-container {
        cursor: pointer;
        img {
          box-sizing: border-box;
        }

        &.selected {
          img {
            border: 5px solid ${Theme.blue};
          }
        }
      }
    }

    .neko-media-library-modal-content-body-selected-gallery {
      display: grid;
      gap: 5px;
      grid-template-columns: repeat(auto-fit, 50px);

      .neko-media-library-modal-content-selected-media-container {
        overflow: hidden;
        position: relative;
        height: 50px;
        width: 50px;

        img {
          box-sizing: border-box;
          width: 100%;
        }

        &:hover {
          .remove-button {
              display: block;
          }
          img {
              transform: scale(1.2);
          }
        }

        .remove-button {
          background-color: rgba(${Theme.blackRGB}, 0.4);
          border: 0;
          cursor: pointer;
          display: none;
          position: absolute;
          top: 0;
          right: 0;
          width: 50%;
          height: 50%;
        }
      }
    }
  }
  .neko-media-library-modal-content-footer {
    display: flex;
    justify-content: space-between;
  }
  .neko-media-library-modal-search {
    position: relative;
  }
`;

const StyledNekoMediaLibraryModal = (props) => {
  const { images, onNextPage, onPreviousPage, onRefresh, onClick, onSearch, searchValue, busy,
    currentPage, limit, total, onPageChange, selected, multiSelect = false, showUploader = false, uploader, ...rest } = props;
  const [ search, setSerch ] = useState(searchValue);

  const selectedMediaJsx = useMemo( () => {
    if ( !multiSelect ) return [];
    return selected.map( (image) => {
      return (
        <div key={image.id} className="neko-media-library-modal-content-selected-media-container">
          <img src={image.src} className="neko-media-library-modal-content-media" />
          <button className="remove-button" onClick={() => onClick({ id: image.id, src: image.src })}>
            <NekoIcon icon="close" color={Theme.white} width={25} height={25} />
          </button>
        </div>
      );
    } );

  }, [ selected ] );

  const mediaJsx = useMemo(() => {
    return images.map((image) => {
      return (
        <div key={image.id} className={`neko-media-library-modal-content-media-container ${selected.some(v => v.id === image.id) ? 'selected' : ''}`}
          onClick={() => {
            onClick({ id: image.id, src: image.src });
            if (!multiSelect) props.onRequestClose();
        }}>
          <img src={image.src} className="neko-media-library-modal-content-media" width="90" height="90" />
        </div>
      );
    });
  }, [images, selected]);

  const onReset = () => {
    setSerch("");
    onRefresh();
  }

  return (
    <StyledNekoModal {...rest}>
      <div className="neko-media-library-modal-content">
        <div className="neko-media-library-modal-content-header">
          <NekoTypo h1>Media Library</NekoTypo>
          <div style={{ display: 'flex' }}>
            <div className="neko-media-library-modal-search">
              <NekoInput value={search} disabled={busy} onEnter={onSearch} onChange={(v) => setSerch(v)} placeholder="Search..." />
              {search !== "" && <NekoIcon icon="close" containerStyle={{ width: '20px', position: 'absolute', right: '5px', top: '5px', cursor: 'pointer' }} width={20} height={20} color={Theme.darkGray} onClick={onReset} />}
            </div>
            <NekoButton onClick={() => onSearch(search)} style={{ marginLeft: '4px' }}>Search</NekoButton>
          </div>
        </div>
        <div>
          <div className="neko-media-library-modal-content-body">
            <NekoBusyOverlay busy={busy}>
              <div className="neko-media-library-modal-content-body-gallery">
                {showUploader && uploader}
                {mediaJsx}
              </div>
            </NekoBusyOverlay>
            <div className="neko-media-library-modal-content-body-selected-gallery">
              {selectedMediaJsx}
            </div>
          </div>
        </div>
        <div className="neko-media-library-modal-content-footer">
          <NekoPaging limit={limit} currentPage={currentPage} total={total} onClick={onPageChange} />
          <NekoButton onClick={props.onRequestClose}>
            Close
          </NekoButton>
        </div>
      </div>
    </StyledNekoModal>
  );
};

const NekoMediaLibraryModal = (props) => {
  return <StyledNekoMediaLibraryModal {...props} />;
};

NekoMediaLibraryModal.propTypes = {
  images: PropTypes.array,
  onNextPage: PropTypes.func,
  onPreviousPage: PropTypes.func,
  onRefresh: PropTypes.func,
  onClick: PropTypes.func,
  onSearch: PropTypes.func,
  searchValue: PropTypes.string,
  busy: PropTypes.bool,
  currentPage: PropTypes.number,
  limit: PropTypes.number,
  total: PropTypes.number,
  onPageChange: PropTypes.func,
  multiSelect: PropTypes.bool,
  selected: PropTypes.array
};

NekoMediaLibraryModal.defaultProps = {
  multiSelect: false
};

export { NekoMediaLibraryModal };
